import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from 'react-hook-form';

import CustomBtn from '../widgets/CustomBtn';
import CustomInput from '../widgets/CustomInput';
import AppContext from '../context/AppContext';
import { HeadingContext } from '../context/HeadingContext';
import { changePassword } from '../_slice/userSlice';

import closeEye from '../../assets/images/close_eye.png';
import openEye from '../../assets/images/open_eye.png';
import rightTick from "../../assets/images/tickRight.png";
import crosstick from "../../assets/images/crosssIcon.png";
import { organizationList } from '../_slice/organizationSlice';
import { toast } from 'react-toastify';
import Auth from '../../auth/Auth';

function Setting() {

  const { setLoader } = useContext(AppContext);
  const { setHeading } = useContext(HeadingContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isCurrentPasswordFocused, setIsCurrentPasswordFocused] = useState(false);
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);
  const [isConfirmPasswordFocused, setIsConfirmPasswordFocused] = useState(false);

  // Show/hide password state
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      old_password: '',
      new_password: '',
      new_password_confirmation: ''
    }
  });

  // Watch password values for comparison
  const watchCurrentPassword = watch('old_password');
  const watchNewPassword = watch('new_password');
  const watchPasswordConfirmation = watch('new_password_confirmation');

  const organizationStatus = useSelector(
    (state) => state?.organization?.status
  );

  useEffect(() => {
    if (organizationStatus === "idle") {
      dispatch(organizationList({ setLoader, navigate }));
    }
  }, [organizationStatus, dispatch, setLoader, navigate]);

  useEffect(() => {
    setHeading("Settings");
  }, [setHeading, location?.state?.orgId]);

  // Password strength validation function
  const isPasswordValid = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`])[A-Za-z\d!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]{12,}$/.test(password);

  const getColor = (password) => (password === '' ? '' : isPasswordValid(password) ? '#292929' : 'red');

  const getValidationMessage = (regex, message, password) => {
    const isValid = regex.test(password);
    const icon = isValid ? <img src={rightTick} alt='' /> : <img src={crosstick} alt='' />;
    const space = '\u00A0\u00A0';
    const colorStyle = isValid ? { color: '#05C680' } : { color: getColor(password) };

    return (
      <div style={{ ...colorStyle, marginTop: "15px" }} >
        {icon} {space} {message}
      </div>
    );
  };

  // Check if all password conditions are met
  const allConditionsMetNew = isPasswordValid(watch('new_password'));
  const allConditionsMetConfirm = isPasswordValid(watch('new_password_confirmation'));

  // Modify the onSubmit function to pass data directly
  const onSubmit = (data) => {
    if (!isPasswordValid(data.new_password)) {
      setIsNewPasswordFocused(true);
      return;
    }
    if (!isPasswordValid(data.new_password_confirmation)) {
      setIsConfirmPasswordFocused(true);
      return;
    }

    // Pass the fields directly instead of nesting in initialPost
    dispatch(
      changePassword({
        old_password: data.old_password,
        new_password: data.new_password,
        new_password_confirmation: data.new_password_confirmation,
        navigate,
        setLoader
      })
    )
      .unwrap()
      .then(() => {
        toast.success('Password changed successfully!');
        Auth.logout(); // Clear authentication
        navigate('/'); // Redirect to home
      })
      .catch((error) => {
        toast.error(error || 'Failed to change password');
      });

  };

  return (
    <div className='col-lg-6'>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h1 className="org_header_heading">Change Password</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='row'>
          {/* Current Password */}
          <div className='col-lg-6 mb-3 position-relative'>
            <div className='form-group'>
              <label className='innerlable'>Current Password</label>
              <CustomInput
                type={showPassword.currentPassword ? "text" : "password"}
                icon={true}
                src={showPassword.currentPassword ? openEye : closeEye}
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    currentPassword: !showPassword.currentPassword,
                  }))
                }
                className='form-control'
                placeholder='Current Password'
                {...register('old_password', {
                  required: 'Current password is required',
                })}
                onFocus={() => setIsCurrentPasswordFocused(true)}
                onBlur={() => setIsCurrentPasswordFocused(false)}
              />
            </div>
            {errors.old_password && <p className='text-danger'>{errors.old_password.message}</p>}
          </div>

          {/* New Password */}
          <div className='col-lg-6 mb-3 position-relative'>
            <div className='form-group'>
              <label className='innerlable'>New Password</label>
              <CustomInput
                type={showPassword.newPassword ? "text" : "password"}
                icon={true}
                src={showPassword.newPassword ? openEye : closeEye}
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    newPassword: !showPassword.newPassword,
                  }))
                }
                className='form-control'
                placeholder='New Password'
                {...register('new_password', {
                  required: 'New password is required',
                })}
                onFocus={() => setIsNewPasswordFocused(true)}
                onBlur={() => setIsNewPasswordFocused(false)}
              />
            </div>
            {isNewPasswordFocused && !allConditionsMetNew && (
              <div style={{ color: getColor(watchNewPassword) }} className='password_strengthbox'>
                <b className='password_heading mb-4'>Password must be</b>
                {getValidationMessage(/.{12,}$/, ' Minimum 12 characters', watchNewPassword)}
                {getValidationMessage(/(?=.*[a-z])/, ' At least one lowercase letter', watchNewPassword)}
                {getValidationMessage(/(?=.*[A-Z])/, ' At least one uppercase letter', watchNewPassword)}
                {getValidationMessage(/(?=.*\d)/, ' At least one number', watchNewPassword)}
                {getValidationMessage(/[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]/, 'At least one special character', watchNewPassword)}
              </div>
            )}
            {errors.new_password && <p className='text-danger'>{errors.new_password.message}</p>}
          </div>

          {/* Confirm Password */}
          <div className='col-lg-6 mb-3 position-relative'>
            <div className='form-group'>
              <label className='innerlable'>Confirm Password</label>
              <CustomInput
                type={showPassword.confirmPassword ? "text" : "password"}
                icon={true}
                src={showPassword.confirmPassword ? openEye : closeEye}
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    confirmPassword: !showPassword.confirmPassword,
                  }))
                }
                className='form-control'
                placeholder='Confirm Password'
                {...register('new_password_confirmation', {
                  required: 'Confirm Password is required',
                  validate: value =>
                    value === watchNewPassword || 'Passwords do not match',
                })}
                onFocus={() => setIsConfirmPasswordFocused(true)}
                onBlur={() => setIsConfirmPasswordFocused(false)}
              />
            </div>
            {isConfirmPasswordFocused && !allConditionsMetConfirm && (
              <div style={{ color: getColor(watchPasswordConfirmation) }} className='password_strengthbox'>
                <b className='password_heading mb-4'>Password must be</b>
                {getValidationMessage(/.{12,}$/, ' Minimum 12 characters', watchPasswordConfirmation)}
                {getValidationMessage(/(?=.*[a-z])/, ' At least one lowercase letter', watchPasswordConfirmation)}
                {getValidationMessage(/(?=.*[A-Z])/, ' At least one uppercase letter', watchPasswordConfirmation)}
                {getValidationMessage(/(?=.*\d)/, ' At least one number', watchPasswordConfirmation)}
                {getValidationMessage(/[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]/, 'At least one special character', watchPasswordConfirmation)}
              </div>
            )}
            {errors.new_password_confirmation && <p className='text-danger'>{errors.new_password_confirmation.message}</p>}
          </div>

          <div className="col-lg-6">
          </div>

          <div className="col-lg-6">
            <CustomBtn
              buttonText='Save'
              className='mt-4'
              type='submit'
            />
          </div>
        </div>
      </form>
    </div>
  )
}

export default Setting;